import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import NewsCard from '@/components/feature/NewsCard';
import ShareDialog from './components/ShareDialog';
import { findComic, findNews, getRelatedComics, getRelatedNews } from '@/utils/content';
import { useFavorites } from '@/hooks/useFavorites';

export default function DetailPage() {
  const { type = 'comics', id = '' } = useParams();
  const [shareOpen, setShareOpen] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  const comic = type === 'comics' ? findComic(id) : undefined;
  const article = type === 'news' ? findNews(id) : undefined;

  const relatedComics = useMemo(() => (comic ? getRelatedComics(comic) : []), [comic]);
  const relatedNews = useMemo(() => (article ? getRelatedNews(article) : []), [article]);

  if (!comic && !article) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <title>内容不存在 - 漫蛙动漫频道</title>
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="flex max-w-md flex-col items-center text-center">
            <span className="mw-float flex h-20 w-20 items-center justify-center rounded-full bg-primary-100 text-4xl text-primary-600">
              <i className="ri-emotion-sad-line" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-800 text-foreground-950">内容不存在或已下架</h1>
            <p className="mt-3 text-sm font-label text-foreground-500">
              你访问的页面可能已经被移除，或者链接出现了问题。
            </p>
            <Link
              to="/"
              className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-label font-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (comic) {
    const favorite = isFavorite(comic.id);
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <title>{`${comic.title} - 漫蛙漫画动漫频道`}</title>
        <meta name="description" content={`${comic.title}：${comic.summary.slice(0, 70)}`} />
        <meta name="keywords" content={`${comic.title},${comic.genre},${comic.tags.join(',')},漫蛙,漫画`} />

        <SiteHeader />

        <main className="flex-1">
          <div className="mx-auto w-full max-w-[1440px] px-4 pt-6 md:px-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '漫画分类', to: '/category/comics' },
                { label: comic.title },
              ]}
            />
          </div>

          <section className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8">
            <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:grid-cols-[300px_1fr]">
              <div className="mx-auto w-full max-w-[300px] lg:mx-0">
                <div className="aspect-[3/4] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
                  <img src={comic.cover} alt={`${comic.title}漫画封面`} className="h-full w-full object-cover object-top" />
                </div>
              </div>

              <div className="flex flex-col">
                <h1 className="font-heading text-2xl font-800 text-foreground-950 md:text-3xl">{comic.title}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Link
                    to={`/category/comics?genre=${encodeURIComponent(comic.genre)}`}
                    className="cursor-pointer rounded-md bg-primary-100 px-3 py-1 text-xs font-label font-500 text-primary-800 transition-colors hover:bg-primary-200"
                  >
                    {comic.genre}
                  </Link>
                  <span className="rounded-md bg-secondary-100 px-3 py-1 text-xs font-label font-500 text-secondary-800">
                    {comic.status}
                  </span>
                  <span className="rounded-md bg-background-100 px-3 py-1 text-xs font-label text-foreground-600">
                    {comic.region}
                  </span>
                  <span className="flex items-center gap-1 rounded-md bg-accent-100 px-3 py-1 text-xs font-label font-500 text-accent-900">
                    <i className="ri-star-fill" />
                    {comic.rating}
                  </span>
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                  <InfoItem label="作者" value={comic.author} />
                  <InfoItem label="连载状态" value={comic.status} />
                  <InfoItem label="地区" value={comic.region} />
                  <InfoItem label="更新时间" value={comic.updatedAt} />
                  <InfoItem label="收藏数" value={`${(comic.favorites / 1000).toFixed(1)}k`} />
                  <InfoItem label="热度" value={`${comic.popularity}`} />
                </dl>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => toggleFavorite(comic.id)}
                    className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-label font-500 transition-colors ${
                      favorite
                        ? 'bg-accent-500 text-foreground-950 hover:bg-accent-400'
                        : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                    }`}
                  >
                    <i className={favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                    {favorite ? '已收藏' : '收藏作品'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-label font-500 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                  >
                    <i className="ri-share-forward-line" />
                    分享
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="mx-auto w-full max-w-[1440px] px-4 pb-10 md:px-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="mb-4 flex items-center gap-2.5 font-heading text-xl font-800 text-foreground-950">
                <span className="flex h-6 w-1.5 rounded-full bg-accent-500" />
                作品简介
              </h2>
              <p className="text-sm leading-loose text-foreground-700">{comic.summary}</p>
              <p className="mt-4 text-sm leading-loose text-foreground-700">
                本作以细腻的画面与扎实的叙事见长，围绕主角的成长与抉择展开。随着剧情推进，作品逐步揭开更深层的世界观，
                为读者带来兼具热血与温度的故事体验。
              </p>

              <div className="mt-6 border-t border-background-200 pt-5">
                <h3 className="mb-3 text-sm font-700 text-foreground-800">相关标签</h3>
                <div className="flex flex-wrap gap-2">
                  {comic.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="cursor-pointer rounded-md bg-background-100 px-3 py-1.5 text-xs font-label text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mx-auto w-full max-w-[1440px] px-4 pb-16 md:px-8">
            <h2 className="mb-6 flex items-center gap-2.5 font-heading text-xl font-800 text-foreground-950">
              <span className="flex h-6 w-1.5 rounded-full bg-accent-500" />
              相关推荐
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 lg:gap-5">
              {relatedComics.map((item) => (
                <ComicCard key={item.id} comic={item} />
              ))}
            </div>
          </section>
        </main>

        <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={comic.title} />
        <SiteFooter />
      </div>
    );
  }

  const favorite = isFavorite(article!.id);
  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <title>{`${article!.title} - 漫蛙漫画动漫频道`}</title>
      <meta name="description" content={article!.summary.slice(0, 80)} />
      <meta name="keywords" content={`${article!.title},${article!.tags.join(',')},漫蛙,动漫资讯`} />

      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1440px] px-4 pt-6 md:px-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '动漫资讯', to: '/category/news' },
              { label: article!.category },
            ]}
          />
        </div>

        <article className="mx-auto w-full max-w-[900px] px-4 py-6 md:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/category/news?category=${encodeURIComponent(article!.category)}`}
              className="cursor-pointer rounded-md bg-accent-500 px-3 py-1 text-xs font-label font-700 text-foreground-950 transition-colors hover:bg-accent-400"
            >
              {article!.category}
            </Link>
            <span className="text-xs font-label text-foreground-500">{article!.date}</span>
            <span className="text-xs font-label text-foreground-500">· {article!.source}</span>
          </div>

          <h1 className="mt-3 font-heading text-2xl font-800 leading-snug text-foreground-950 md:text-3xl">
            {article!.title}
          </h1>
          <p className="mt-3 text-sm font-label text-foreground-500">
            作者：{article!.author} · 发布：{article!.date}
          </p>

          <div className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img src={article!.cover} alt={`${article!.title}配图`} className="h-full w-full object-cover object-top" />
          </div>

          <div className="mt-7 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            {article!.content.map((paragraph, index) => (
              <p key={index} className="mb-4 text-sm leading-loose text-foreground-700 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
            <div className="flex flex-wrap gap-2">
              {article!.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="cursor-pointer rounded-md bg-background-50 px-3 py-1.5 text-xs font-label text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  #{tag}
                </Link>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => toggleFavorite(article!.id)}
                className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-label font-500 transition-colors ${
                  favorite ? 'bg-accent-500 text-foreground-950 hover:bg-accent-400' : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                <i className={favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                {favorite ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-label font-500 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>
          </div>
        </article>

        <section className="mx-auto w-full max-w-[1440px] px-4 pb-16 md:px-8">
          <h2 className="mb-6 flex items-center gap-2.5 font-heading text-xl font-800 text-foreground-950">
            <span className="flex h-6 w-1.5 rounded-full bg-accent-500" />
            相关资讯
          </h2>
          <div className="flex flex-col gap-4">
            {relatedNews.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      </main>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={article!.title} />
      <SiteFooter />
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-label text-foreground-500">{label}</dt>
      <dd className="mt-0.5 font-500 text-foreground-900">{value}</dd>
    </div>
  );
}